import {
  ArrowDropDown,
  ArrowDropUp,
  Person,
  Place,
  ScheduleOutlined,
  Search,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  Grid,
  InputBase,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { Dayjs } from "dayjs";
import {
  homepageActions,
  selectConditions,
  selectScenarios,
} from "features/homepage/saga/homepageSlice";
import AreaPickerButton from "features/searchStore/components/AreaPickerButton";
import DatePickerButton from "features/searchStore/components/DatePickerButton";
import GenrePickerButton from "features/searchStore/components/GenrePickerButton";
import LabelCheckbox from "features/searchStore/components/LabelCheckbox";
import SelectableBox from "features/searchStore/components/SelectableBox";
import SuggestSearchBar from "features/searchStore/components/SuggestSearchBar";
import { ISearchBudget, ISearchFilter } from "features/searchStore/models";
import {
  searchStoreActions,
  selectFilter,
} from "features/searchStore/saga/searchStoreSlice";
import { generateBudgetOptions } from "helpers/generateBudgetOptions";
import { generatePeopleNumberOpts } from "helpers/generatePeopleNumerOption";
import { generateTimeOptions } from "helpers/generateTimeOptions";
import { ReactNode, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { IValueLabel } from "utils/commonModels";

export function SideContentSection() {
  const dispatch = useAppDispatch();
  const filter = useAppSelector(selectFilter);
  const conditionList = useAppSelector(selectConditions);
  const scenarioList = useAppSelector(selectScenarios);

  const [selectedArea, setSelectedArea] = useState<
    IValueLabel<string> | null | undefined
  >(null);
  const [dateData, setDateData] = useState<Dayjs | null | undefined>(null);
  const [timeData, setTimeData] = useState<string | null | undefined>(
    "指定なし"
  );
  const [peopleNumber, setPeopleNumber] = useState<number | null | undefined>(
    2
  );
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedScenes, setSelectedScenes] = useState<string[]>([]);

  const [moreCondition, setMoreCondition] = useState(false);
  const [moreScene, setMoreScene] = useState(false);

  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(0);

  const handleMoreCondition = () => {
    const prev = moreCondition;
    setMoreCondition(!prev);
  };

  const handleMoreScene = () => {
    const prev = moreScene;
    setMoreScene(!prev);
  };

  const handleChangeArea = (value: IValueLabel<string> | null) => {
    setSelectedArea(value);
  };

  const handleChangeDate = (value: Dayjs | null | undefined) => {
    setDateData(value);
  };

  const handleChangeGenre = (e: any) => {
    setSelectedGenres(e);
  }

  const handleSelectCondition = (e: any) => {
    if (!selectedConditions.includes(e)) {
      setSelectedConditions((pre) => [...pre, e]);
    } else {
      const remain = selectedConditions.filter((id) => id !== e);
      setSelectedConditions(remain);
    }
  };

  const handleSelectScene = (e: any) => {
    if (!selectedScenes.includes(e)) {
      setSelectedScenes((pre) => [...pre, e]);
    } else {
      const remain = selectedScenes.filter((id) => id !== e);
      setSelectedScenes(remain);
    }
  };

  const handleSearchCondition = () => {
    const newFilter: ISearchFilter = {
      ...filter,
      conditions: selectedConditions,
    };
    dispatch(searchStoreActions.setSearchFilter(newFilter));
  };

  const handleSearchScenario = () => {
    const newFilter: ISearchFilter = {
      ...filter,
      scenes: selectedScenes,
    };
    dispatch(searchStoreActions.setSearchFilter(newFilter));
  };

  const handleSelectTime = (e: any) => {
    setTimeData(e.target.value);
  };

  const handleSelectNumberPeople = (e: any) => {
    setPeopleNumber(e.target.value);
  };

  const handleSearchFilter = () => {
    const newFilter: ISearchFilter = {
      ...filter,
      area: selectedArea,
      date: dateData,
      time: timeData,
      people: peopleNumber,
      genres: selectedGenres,
    };
    dispatch(searchStoreActions.setSearchFilter(newFilter));
  }

  const handleSearchBudget = () => {
    if (minValue !== 0 || maxValue !== 0) {
      const newFilter: ISearchFilter = {
        ...filter,
        minBudget: minValue,
        maxBudget: maxValue,
      };
      const req: ISearchBudget = {
        min: minValue,
        max: maxValue,
        sort_code: filter.sort_code,
      };
      dispatch(searchStoreActions.setSearchFilter(newFilter));
      dispatch(searchStoreActions.searchByBudget(req));
    } else {
      dispatch(searchStoreActions.clearBudgetStore());
    }
  };

  useEffect(() => {
    dispatch(homepageActions.getConditions(0));
    dispatch(homepageActions.getScrenario(0));
    dispatch(homepageActions.getFoodGenres(0));
    dispatch(homepageActions.getDistricts(0));
    setSelectedArea(filter.area);
    setDateData(filter.date);
    setTimeData(filter.time ?? "指定なし");
    setPeopleNumber(filter.people ?? 2);
    setSelectedGenres(filter.genres);
    setSelectedConditions(filter.conditions);
    setSelectedScenes(filter.scenes);
    setMinValue(filter.minBudget);
    setMaxValue(filter.maxBudget);
  }, [dispatch, filter]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      minWidth="150px"
      maxWidth="300px"
    >
      <Box bgcolor="#eee" borderRadius={2} p={2}>
        <Typography fontWeight="bold">エリア</Typography>
        <AreaPickerButton onChange={handleChangeArea} />

        <Typography fontWeight="bold">日付・時間 </Typography>
        <DatePickerButton onChange={handleChangeDate} />

        <FormControl
          sx={{ minWidth: 120, my: 2, display: "flex" }}
          size="small"
          fullWidth
        >
          <Select
            fullWidth
            displayEmpty
            value={timeData}
            sx={{ bgcolor: "white" }}
            onChange={handleSelectTime}
            renderValue={(value: ReactNode) => {
              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "end" }}>
                  <ScheduleOutlined color="primary" fontSize="small" />
                  {value}
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                },
              },
            }}
          >
            <MenuItem value="指定なし">指定なし</MenuItem>
            {generateTimeOptions(7, 24)}
          </Select>
        </FormControl>

        <Typography fontWeight="bold">人数 </Typography>
        <FormControl
          sx={{ minWidth: 120, my: 2, display: "flex" }}
          size="small"
          fullWidth
        >
          <Select
            fullWidth
            displayEmpty
            value={peopleNumber}
            sx={{ bgcolor: "white" }}
            onChange={handleSelectNumberPeople}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                },
              },
            }}
          >
            {generatePeopleNumberOpts(1, 99)}
          </Select>
        </FormControl>

        <Typography fontWeight="bold">料理ジャンル</Typography>
        <GenrePickerButton onChange={handleChangeGenre} />

        <Button
          fullWidth
          variant="contained"
          sx={{
            my: 2,
          }}
          onClick={handleSearchFilter}
        >
          検索する
        </Button>
      </Box>

      <Box flex={1} my={2}>
        <SuggestSearchBar />
      </Box>

      <Box display="flex" flex={1} my={2} alignItems="center">
        <Typography display="flex" flex={1}>
          カジュアルなお店を含む
        </Typography>
        <Switch />
      </Box>

      <Grid container spacing={2} my={1}>
        <Grid item xs={6}>
          <SelectableBox label="朝食" />
        </Grid>

        <Grid item xs={6}>
          <SelectableBox label="ランチ" />
        </Grid>

        <Grid item xs={6}>
          <SelectableBox label="ティー" />
        </Grid>

        <Grid item xs={6}>
          <SelectableBox label="ディナー" />
        </Grid>
      </Grid>

      <Box
        my={1}
        border={1}
        borderColor="#eee"
        borderRadius={2}
        display="flex"
        flexDirection="column"
        bgcolor="white"
      >
        <Box p={1} bgcolor="#eee">
          <Typography fontWeight="bold">予算で探す</Typography>
        </Box>
        <Box display="flex" flexDirection="column" flex={1}>
          <Box display="flex" p={1} alignItems="center">
            <FormControl size="small" fullWidth>
              <Select
                placeholder="Min"
                value={minValue.toString()}
                onChange={(e) => setMinValue(parseInt(e.target.value))}
                fullWidth
                MenuProps={{
                  PaperProps: {
                    style: {
                      scrollbarWidth: "thin",
                    },
                  },
                }}
              >
                <MenuItem value="0">下限なし</MenuItem>
                {generateBudgetOptions()}
              </Select>
            </FormControl>
            <Typography mx={2}>~</Typography>
            <FormControl size="small" fullWidth>
              <Select
                placeholder="Max"
                value={maxValue.toString()}
                onChange={(e) => setMaxValue(parseInt(e.target.value))}
                fullWidth
                MenuProps={{
                  PaperProps: {
                    style: {
                      scrollbarWidth: "thin",
                    },
                  },
                }}
              >
                <MenuItem value="0">上限なし</MenuItem>
                {generateBudgetOptions()}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" p={2} alignItems="center">
            <Checkbox />
            <Typography>お席のみを予約するプランに</Typography>
          </Box>
          <Box display="flex" justifyContent="end" p={1}>
            <Button variant="outlined" onClick={handleSearchBudget}>
              検索
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        my={1}
        border={1}
        borderColor="#eee"
        borderRadius={2}
        display="flex"
        flexDirection="column"
        bgcolor="white"
      >
        <Box
          p={1}
          bgcolor="#eee"
          display="flex"
          sx={{
            cursor: "pointer",
          }}
          onClick={handleMoreCondition}
        >
          <Typography
            fontWeight="bold"
            sx={{
              display: "flex",
              flex: 1,
            }}
          >
            こだわり
          </Typography>
          {moreCondition ? <ArrowDropUp /> : <ArrowDropDown />}
        </Box>
        <Collapse in={moreCondition}>
          <Box display="flex" flexDirection="column" flex={1}>
            <Grid container>
              {conditionList.map((e) => (
                <LabelCheckbox
                  xs={12}
                  p={1}
                  value={e.Id}
                  boxLabel={e.Name}
                  onSelected={handleSelectCondition}
                  defaultState={selectedConditions.includes(e.Id)}
                />
              ))}
            </Grid>

            <Box display="flex" justifyContent="end" p={1}>
              <Button variant="outlined" onClick={handleSearchCondition}>
                検索
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Box
        my={1}
        border={1}
        borderColor="#eee"
        borderRadius={2}
        display="flex"
        flexDirection="column"
        bgcolor="white"
      >
        <Box
          p={1}
          bgcolor="#eee"
          display="flex"
          sx={{
            cursor: "pointer",
          }}
          onClick={handleMoreScene}
        >
          <Typography
            fontWeight="bold"
            sx={{
              display: "flex",
              flex: 1,
            }}
          >
            シーン
          </Typography>
          {moreScene ? <ArrowDropUp /> : <ArrowDropDown />}
        </Box>
        <Collapse in={moreScene}>
          <Box display="flex" flexDirection="column" flex={1}>
            <Grid container>
              {scenarioList.map((e) => (
                <LabelCheckbox
                  xs={12}
                  p={1}
                  value={e.Id}
                  boxLabel={e.Name}
                  defaultState={selectedScenes.includes(e.Id)}
                  onSelected={handleSelectScene}
                />
              ))}
            </Grid>

            <Box display="flex" justifyContent="end" p={1}>
              <Button variant="outlined" onClick={handleSearchScenario}>
                検索
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
